/**
 * Stacking Plan — views/default/post-types/page/layouts/stacking_plan.php
 *
 * - Status filters (`data-stacking-filter`: all | available | under-offer)
 *   hide the other rows and their building shapes; `aria-pressed` and the
 *   button style (`style-default` pressed, `style-reverse` not) follow, and a
 *   polite live region reports the count.
 * - A row and its building overlay shape (`data-stacking-level`) light up
 *   together (`is-highlighted`) on hover or keyboard focus of either.
 * - "View floorplan" (`data-stacking-floorplan`) fills the layout's one
 *   floorplan modal from the level's <template data-stacking-modal> and opens
 *   it with MicroModal; the modal lives on <body> while the page is shown.
 *   Its Enquire link closes the modal, then scrolls to `#enquire`.
 */

import MicroModal from 'micromodal';
import { getLenisInstance, getHeaderOffset } from '../core/scroll-manager.js';

const controllers = new Set();
const movedModals = new Set();

/**
 * Fill the modal from a level's template and open it.
 *
 * @param {HTMLElement} modal   `[data-stacking-modal-root]`
 * @param {HTMLTemplateElement} template
 * @param {HTMLElement} trigger Focus returns here on close.
 */
function openFloorplan(modal, template, trigger) {
    const data = template.content;
    const slot = (name) => modal.querySelector(`[data-slot="${name}"]`);
    const field = (name) => data.querySelector(`[data-field="${name}"]`);

    slot('plan').replaceChildren(data.querySelector('.floorplan-modal__plan-inner').cloneNode(true));
    ['title', 'tenancy', 'nla'].forEach((name) => {
        slot(name).textContent = field(name)?.textContent.trim() ?? '';
    });
    const status = field('status');
    const statusSlot = slot('status');
    statusSlot.textContent = status?.textContent.trim() ?? '';
    statusSlot.className = `stacking-plan__status stacking-plan__status--${status?.dataset.status ?? ''}`;

    MicroModal.show(modal.id, {
        awaitOpenAnimation: true,
        awaitCloseAnimation: true,
        disableScroll: false, // Lenis is stopped instead.
        // MicroModal focuses the first control that isn't a close button (Enquire).
        onShow: () => getLenisInstance()?.stop(),
        onClose: () => {
            getLenisInstance()?.start();
            trigger?.focus?.();
        },
    });
}

/**
 * @param {HTMLElement} root `[data-stacking-plan]`
 * @returns {AbortController}
 */
function setupStackingPlan(root) {
    const abort = new AbortController();
    const { signal } = abort;
    const rows = [...root.querySelectorAll('tr[data-stacking-level]')];
    const shapes = [...root.querySelectorAll('.stacking-plan__shape')];
    const filters = [...root.querySelectorAll('[data-stacking-filter]')];
    const empty = root.querySelector('.stacking-plan__empty');
    const count = root.querySelector('[data-stacking-count]');

    const highlight = (level) => {
        [...rows, ...shapes].forEach((el) => {
            el.classList.toggle('is-highlighted', level !== null && el.dataset.stackingLevel === level);
        });
    };

    const filter = (status) => {
        let shown = 0;
        rows.forEach((row) => {
            const match = status === 'all' || row.dataset.status === status;
            row.hidden = !match;
            if (match) shown++;
        });
        shapes.forEach((shape) => {
            shape.toggleAttribute('hidden', !(status === 'all' || shape.dataset.status === status));
        });
        if (empty) empty.hidden = shown > 0;
        filters.forEach((button) => {
            const on = button.dataset.stackingFilter === status;
            button.setAttribute('aria-pressed', on ? 'true' : 'false');
            const wrap = button.closest('.btn_wrap');
            if (wrap) {
                wrap.classList.toggle('style-default', on);
                wrap.classList.toggle('style-reverse', !on);
            }
        });
        if (count) count.textContent = `${shown} of ${rows.length} levels shown`;
        highlight(null);
    };

    filters.forEach((button) => {
        button.addEventListener('click', () => filter(button.dataset.stackingFilter), { signal });
    });

    // Row ↔ shape highlight: pointer and keyboard.
    const levelOf = (target) => target.closest?.('[data-stacking-level]')?.dataset.stackingLevel ?? null;
    root.addEventListener('pointerover', (event) => highlight(levelOf(event.target)), { signal });
    root.addEventListener('pointerleave', () => highlight(null), { signal });
    root.addEventListener('focusin', (event) => highlight(levelOf(event.target)), { signal });
    root.addEventListener('focusout', (event) => {
        if (!root.contains(event.relatedTarget)) highlight(null);
    }, { signal });

    // Floorplan modal.
    const modal = root.querySelector('[data-stacking-modal-root]');
    if (modal) {
        document.body.appendChild(modal);
        movedModals.add(modal);

        root.addEventListener('click', (event) => {
            const trigger = event.target.closest('[data-stacking-floorplan]');
            if (!trigger) return;
            const template = root.querySelector(`template[data-stacking-modal="${trigger.dataset.stackingFloorplan}"]`);
            if (template) openFloorplan(modal, template, trigger);
        }, { signal });

        modal.querySelector('[data-stacking-enquire]')?.addEventListener('click', (event) => {
            const target = document.getElementById('enquire');
            event.preventDefault();
            event.stopPropagation();
            MicroModal.close(modal.id);
            if (!target) return;
            // After onClose has restarted Lenis.
            requestAnimationFrame(() => {
                const y = target.getBoundingClientRect().top + window.scrollY;
                getLenisInstance()?.scrollTo(target, { offset: getHeaderOffset(y) });
            });
        }, { signal });
    }

    // A shape opens its level's floorplan, when there is one.
    shapes.forEach((shape) => {
        shape.addEventListener('click', () => {
            root.querySelector(`[data-stacking-floorplan="${shape.dataset.stackingLevel}"]`)?.click();
        }, { signal });
    });

    return abort;
}

/**
 * @param {Document|HTMLElement} container Page or Barba container.
 */
export function initializeStackingPlans(container = document) {
    container.querySelectorAll('[data-stacking-plan]').forEach((root) => {
        if (root.dataset.stackingReady) return;
        root.dataset.stackingReady = 'true';
        controllers.add(setupStackingPlan(root));
    });
}

export function destroyStackingPlans() {
    controllers.forEach((abort) => abort.abort());
    controllers.clear();
    movedModals.forEach((modal) => modal.remove());
    movedModals.clear();
    document.querySelectorAll('[data-stacking-plan]').forEach((root) => delete root.dataset.stackingReady);
}
